/**
 * Asistente «Buscar en Twilio → Conectar» (SPEC-estadisticas-conexion §A.4 y §B.7.4).
 *
 * 1. Credenciales: Account SID y Auth Token → «Buscar en Twilio» (`descubrir`, no escribe nada).
 * 2. Servicio y número: viene preseleccionada la sugerencia del backend (si se puede usar).
 * 3. Conectar: la lista de pasos con su resultado. Si la entrada del servicio es de otra app, se
 *    pide confirmar antes de pisarla y se reenvía con `reemplazarEntrada: true`.
 *
 * El Auth Token es un secreto. Se escribe en un input `password` y sale del estado de React apenas
 * se usa en «Buscar en Twilio»: de ahí hasta «Conectar» vive en un `ref` (no se pinta, no queda en
 * el DOM ni en las devtools de React) y se borra cuando termina la conexión. Si el operador se va
 * de la pantalla, se va con el componente.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, KeyRound, Link2, Loader2, PlugZap, RefreshCw, Search, Store } from "lucide-react";
import type { CuentaPublica, Descubrimiento, Diagnostico, PasoConexion, ResultadoConexion } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import {
  armarConectar, armarDescubrir, avisoOtroModo, conexionConErrores, opcionNumero, pasoAConfirmar, resumenConexion, rotuloCuentaTwilio, seleccionInicial,
  SERVICIO_NUEVO, TEXTO_CUENTA_EN_OTRO_COMERCIO, TEXTO_MARCA_DEMO, textoAdvertencia, textoCuentaAtada, textoErrorConexion, validarTokenNuevo,
  nombreServicioNuevo,
} from "../../logic/conexion";
import { leerError, leerRespuesta } from "../../logic/respuesta";
import { usd } from "../../logic/formato";
import { Tarjeta } from "../Tarjeta";
import { ListaPasosConexion, useAutoconfigurar } from "./PanelDiagnostico";
import { ModalReemplazarEntrada } from "./ModalReemplazarEntrada";
import { activaApi } from "../../../campana-nueva/api";
import SelectorMarca from "../../../campana-nueva/components/SelectorMarca";

/** SIDs que acepta el simulador (A1-19): sirven para probar la pantalla sin cuenta de Twilio. */
const SID_PRUEBA = "AC" + "0".repeat(32);
const TOKEN_PRUEBA = "0".repeat(32);

export function AsistenteConexion({ tokenAbierto, onTokenAbierto, onDiagnostico }: {
  tokenAbierto: boolean;
  onTokenAbierto: (v: boolean) => void;
  onDiagnostico: (d: Diagnostico) => void;
}) {
  const { cuenta, modo, recargar } = useCuentaOficial();
  const simulado = modo === "simulated";
  const bloqueadoDemo = !simulado && !!cuenta?.marcaDemoBloqueada;
  // Verificada en simulado con el servidor en twilio: no está conectada (se muestra el formulario) y se dice por qué.
  const conectada = resumenConexion(cuenta);
  const otroModo = avisoOtroModo(cuenta);
  const [otraCuenta, setOtraCuenta] = useState(false);

  // Paso 1
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const tokenEnUso = useRef("");
  const [errores, setErrores] = useState<{ accountSid?: string; authToken?: string }>({});
  const [buscando, setBuscando] = useState(false);
  const [errorBuscar, setErrorBuscar] = useState("");
  const [ayuda, setAyuda] = useState(false);

  // Paso 2
  const [desc, setDesc] = useState<Descubrimiento | null>(null);
  const [senderSid, setSenderSid] = useState("");
  const [servicio, setServicio] = useState("");
  const sidBuscado = useRef("");

  // Paso 3
  const [conectando, setConectando] = useState(false);
  const [errorConectar, setErrorConectar] = useState("");
  const [pasos, setPasos] = useState<PasoConexion[] | null>(null);
  const [urlAConfirmar, setUrlAConfirmar] = useState<string | null>(null);

  const auto = useAutoconfigurar((r) => onDiagnostico(r.diagnostico));

  // El token nunca sobrevive al componente.
  useEffect(() => () => { tokenEnUso.current = ""; }, []);

  const mostrarFormulario = !conectada || otraCuenta;
  const marca = activaApi.nombreActual();
  const brandId = activaApi.marcaActual();

  const buscar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (buscando || bloqueadoDemo) return;
    const a = armarDescubrir({ accountSid, authToken }, { tokenGuardado: !!cuenta?.authTokenGuardado && !otraCuenta });
    if (!a.ok) { setErrores(a.errores); return; }
    setErrores({});
    setErrorBuscar("");
    setBuscando(true);
    setPasos(null);
    setErrorConectar("");
    // El token sale del estado ANTES de esperar la respuesta: no se vuelve a pintar en el input.
    const token = a.entrada.authToken || "";
    tokenEnUso.current = token;
    setAuthToken("");
    try {
      const l = leerRespuesta<Descubrimiento>(await oficialApi.descubrir(a.entrada));
      if (!l.ok) {
        tokenEnUso.current = "";
        setDesc(null);
        setErrorBuscar(textoErrorConexion({ ...l, codigoTwilio: l.extra.codigoTwilio }));
        return;
      }
      if (!l.data || !Array.isArray(l.data.numeros) || !Array.isArray(l.data.servicios)) {
        tokenEnUso.current = "";
        setErrorBuscar("El servidor no devolvió lo que encontró en Twilio.");
        return;
      }
      sidBuscado.current = a.entrada.accountSid;
      setDesc(l.data);
      const s = seleccionInicial(l.data);
      setSenderSid(s.senderSid);
      setServicio(s.servicio);
    } catch (e) {
      tokenEnUso.current = "";
      const x = leerError(e);
      setErrorBuscar(textoErrorConexion({ ...x, codigoTwilio: x.body.codigoTwilio }));
    } finally {
      setBuscando(false);
    }
  };

  const conectar = async (reemplazarEntrada: boolean) => {
    if (conectando || !desc) return;
    const a = armarConectar({ accountSid: sidBuscado.current, authToken: tokenEnUso.current, senderSid, servicio, marca, reemplazarEntrada });
    if (!a.ok) { setErrorConectar(a.error); return; }
    setConectando(true);
    setErrorConectar("");
    let terminado = true;
    try {
      const l = leerRespuesta<ResultadoConexion>(await oficialApi.conectar(a.entrada));
      if (!l.ok) {
        setErrorConectar(textoErrorConexion({ ...l, codigoTwilio: l.extra.codigoTwilio }));
        return;
      }
      if (!l.data || !Array.isArray(l.data.pasos)) { setErrorConectar("El servidor no devolvió el resultado de la conexión."); return; }
      setPasos(l.data.pasos);
      if (l.data.diagnostico) onDiagnostico(l.data.diagnostico);
      const aConfirmar = reemplazarEntrada ? null : pasoAConfirmar(l.data.pasos);
      if (aConfirmar) {
        // Se conserva el token sólo para el reintento con `reemplazarEntrada`.
        terminado = false;
        setUrlAConfirmar(aConfirmar.urlAnterior || "");
      } else if (!conexionConErrores(l.data.pasos)) {
        setDesc(null);
        setOtraCuenta(false);
        setAccountSid("");
      }
      await recargar();
    } catch (e) {
      const x = leerError(e);
      setErrorConectar(textoErrorConexion({ ...x, codigoTwilio: x.body.codigoTwilio }));
    } finally {
      if (terminado) tokenEnUso.current = "";
      setConectando(false);
    }
  };

  const confirmarEntrada = () => { setUrlAConfirmar(null); void conectar(true); };
  const cancelarEntrada = () => { setUrlAConfirmar(null); tokenEnUso.current = ""; };

  const operativos = desc ? desc.numeros.filter((n) => n.operativo === true).length : 0;
  // La cuenta ya es de otro comercio: «Conectar» terminaría en ACCOUNT_SID_EN_USO. Se dice al buscar y no se deja
  // conectar (el aviso de la advertencia lo repetiría: se saca de la lista).
  const enOtroComercio = !simulado && desc?.enOtroComercio === true;

  return (
    <Tarjeta titulo="Conectar con Twilio" right={<PlugZap size={16} className="text-slate-400" />}>
      <div className="space-y-5">
        {simulado && (
          <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
            Modo simulado: se conecta una cuenta de mentira para probar la pantalla.
          </p>
        )}
        {/* El comercio de demostración no carga credenciales reales: se dice al entrar y se ofrece el selector
            ahí mismo. Antes la pantalla dejaba pegar el SID y el token, y recién «Buscar en Twilio» contestaba
            con un 422 MARCA_DEMO. El formulario queda a la vista, apagado, para que se vea qué viene después. */}
        {bloqueadoDemo && (
          <div role="status" className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <Store size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0 space-y-2">
              <p className="font-semibold">{TEXTO_MARCA_DEMO}</p>
              <p className="flex flex-wrap items-center gap-2">
                <span>Elegilo acá</span>
                <SelectorMarca />
                <span className="text-amber-800/80">o en el menú, en «Marca».</span>
              </p>
            </div>
          </div>
        )}
        {otroModo && !simulado && (
          <p className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
            <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> {otroModo}
          </p>
        )}

        {conectada && !otraCuenta && (
          <ResumenConectada cuenta={cuenta!} texto={conectada} simulado={simulado} ocupadoAuto={auto.ocupado}
            onCambiarToken={() => onTokenAbierto(!tokenAbierto)} onOtraCuenta={() => { setOtraCuenta(true); onTokenAbierto(false); }}
            onReconfigurar={() => void auto.correr(false)} />
        )}
        {/* «Cambiar token» también sin la cuenta en verde: justo cuando Twilio rechaza el token viejo
            la cuenta queda en error, y ahí es cuando más hace falta (el diagnóstico lo ofrece). */}
        {tokenAbierto && !simulado && !otraCuenta && !!cuenta?.authTokenGuardado && <FormularioToken onCerrar={() => onTokenAbierto(false)} />}
        {tokenAbierto && !simulado && !cuenta?.authTokenGuardado && (
          <p className="text-sm text-slate-700 bg-slate-100 rounded-lg px-3 py-2">Todavía no hay un token guardado: pegá el Account SID y el Auth Token en el paso 1 y tocá «Buscar en Twilio».</p>
        )}
        {auto.error && <p role="alert" className="text-sm text-red-600">{auto.error}</p>}
        {auto.pasos && <ListaPasosConexion pasos={auto.pasos} />}

        {mostrarFormulario && (
          <>
            {/* Paso 1 */}
            <form onSubmit={buscar} className="space-y-3" autoComplete="off">
              <p className="text-xs font-bold uppercase tracking-wider text-violet-600">Paso 1 · Credenciales</p>
              <fieldset disabled={bloqueadoDemo || buscando} className="grid sm:grid-cols-2 gap-4 disabled:opacity-60">
                <Campo label="Account SID" value={accountSid} onChange={(v) => {
                  setAccountSid(v.trim());
                  setErrores((e) => ({ ...e, accountSid: undefined }));
                  // Otra cuenta: lo encontrado (y el token en uso) era de la anterior.
                  if (desc) { setDesc(null); tokenEnUso.current = ""; }
                }}
                  placeholder={cuenta?.accountSidEnmascarado || "AC…"} error={errores.accountSid} />
                <Campo label="Auth Token" tipo="password" value={authToken}
                  onChange={(v) => { setAuthToken(v.trim()); setErrores((e) => ({ ...e, authToken: undefined })); }}
                  placeholder={cuenta?.authTokenGuardado && !otraCuenta ? "•••••••• (vacío = usar el guardado)" : "32 caracteres"}
                  error={errores.authToken} />
              </fieldset>
              {errorBuscar && <p role="alert" className="text-sm text-red-600">{errorBuscar}</p>}
              <div className="flex items-center gap-3 flex-wrap">
                <button type="submit" disabled={bloqueadoDemo || buscando}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {buscando ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Buscar en Twilio
                </button>
                {simulado && (
                  <button type="button" onClick={() => { setAccountSid(SID_PRUEBA); setAuthToken(TOKEN_PRUEBA); setErrores({}); }}
                    className="text-sm font-semibold text-slate-600 hover:text-slate-900">
                    Completar con datos de prueba
                  </button>
                )}
                {otraCuenta && (
                  <button type="button" onClick={() => { setOtraCuenta(false); setDesc(null); setAuthToken(""); tokenEnUso.current = ""; }}
                    className="text-sm font-semibold text-slate-600 hover:text-slate-900">Cancelar</button>
                )}
                <button type="button" onClick={() => setAyuda((v) => !v)} aria-expanded={ayuda} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
                  {ayuda ? <ChevronDown size={14} /> : <ChevronRight size={14} />} ¿Dónde los encuentro?
                </button>
              </div>
              {ayuda && (
                <ol className="list-decimal pl-5 text-sm text-slate-600 space-y-1">
                  <li>En la consola de Twilio, entrá a la cuenta (o subcuenta) de la marca.</li>
                  <li>Account → API keys & tokens: copiá el Account SID (empieza con AC) y el Auth Token.</li>
                  <li>No sirve una API Key estándar: hace falta el Auth Token.</li>
                </ol>
              )}
            </form>

            {/* Paso 2 */}
            {desc && (
              <div className="space-y-3 border-t border-slate-100 pt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-violet-600">Paso 2 · Servicio y número</p>
                <p className="text-sm text-slate-700">
                  Cuenta <span className="font-semibold">{desc.cuenta.nombre || desc.cuenta.sid}</span>
                  {desc.cuenta.tipo ? ` (${desc.cuenta.tipo})` : ""} · {desc.cuenta.estado || "sin estado"}
                  {desc.cuenta.esSubcuenta ? " · subcuenta" : ""}
                  {desc.saldo && desc.saldo.valor !== null ? ` · saldo ${desc.saldo.moneda === "USD" || !desc.saldo.moneda ? usd(desc.saldo.valor) : `${desc.saldo.moneda} ${desc.saldo.valor}`}` : ""}
                  {desc.simulado ? " · simulada" : ""}
                </p>
                {enOtroComercio && (
                  <p role="alert" className="flex items-start gap-1.5 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">
                    <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> {TEXTO_CUENTA_EN_OTRO_COMERCIO}
                  </p>
                )}
                {desc.advertencias.length > 0 && (
                  <ul className="space-y-1.5">
                    {desc.advertencias.filter((a) => !(enOtroComercio && /otro comercio/.test(a))).map((a) => (
                      <li key={a} className="flex items-start gap-1.5 text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
                        <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> {textoAdvertencia(a, desc)}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectNumero desc={desc} valor={senderSid} onChange={setSenderSid} />
                  <SelectServicio desc={desc} valor={servicio} onChange={setServicio} marca={marca} />
                </div>
                {desc.sugerencia?.motivo && <p className="text-xs text-slate-500">Sugerido: {desc.sugerencia.motivo}</p>}
                {operativos === 0 && desc.numeros.length > 0 && (
                  <p className="text-xs text-slate-500">Ningún número se puede elegir hasta que esté activo en Twilio.</p>
                )}

                {/* Paso 3 */}
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-violet-600">Paso 3 · Conectar</p>
                  {/* Antes de conectar, a qué comercio queda atada la cuenta: desde la app no se desconecta. En
                      simulado no se guarda el SID, así que no ata nada. */}
                  {!simulado && !enOtroComercio && (
                    <p role="note" className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      <Link2 size={15} className="flex-shrink-0 mt-0.5 text-slate-400" aria-hidden="true" />
                      <span>{textoCuentaAtada({ accion: "conectar", comercio: marca, brandId, cuenta: rotuloCuentaTwilio(desc.cuenta) })}</span>
                    </p>
                  )}
                  {errorConectar && <p role="alert" className="text-sm text-red-600">{errorConectar}</p>}
                  <button type="button" onClick={() => void conectar(false)} disabled={conectando || !senderSid || !servicio || enOtroComercio}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
                    {conectando ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Conectar
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {pasos && (
          <div className="rounded-xl border border-slate-100 p-4 space-y-2">
            <p className={`text-sm font-semibold ${conexionConErrores(pasos) ? "text-red-700" : pasoAConfirmar(pasos) ? "text-amber-800" : "text-emerald-700"}`}>
              {conexionConErrores(pasos) ? "La conexión quedó a medias: revisá los pasos en rojo."
                : pasoAConfirmar(pasos) ? "Falta confirmar la entrada de las respuestas."
                : "Conectada. Revisá el diagnóstico de abajo."}
            </p>
            <ListaPasosConexion pasos={pasos} />
          </div>
        )}
      </div>

      {urlAConfirmar !== null && (
        <ModalReemplazarEntrada urlAnterior={urlAConfirmar} busy={conectando} onConfirmar={confirmarEntrada} onCancelar={cancelarEntrada} />
      )}
      {auto.urlAConfirmar !== null && (
        <ModalReemplazarEntrada urlAnterior={auto.urlAConfirmar} busy={auto.ocupado} onConfirmar={auto.confirmar} onCancelar={auto.cancelar} />
      )}
    </Tarjeta>
  );
}

function ResumenConectada({ cuenta, texto, simulado, ocupadoAuto, onCambiarToken, onOtraCuenta, onReconfigurar }: {
  cuenta: CuentaPublica; texto: string; simulado: boolean; ocupadoAuto: boolean;
  onCambiarToken: () => void; onOtraCuenta: () => void; onReconfigurar: () => void;
}) {
  return (
    <div className="rounded-xl bg-emerald-50 px-4 py-3 space-y-3">
      <p className="text-sm text-emerald-800 inline-flex items-start gap-1.5">
        <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" /> {texto}
      </p>
      {cuenta.twilio?.esSubcuenta && <p className="text-xs text-emerald-800/80">Es una subcuenta: el saldo de Twilio se ve en la consola de la cuenta principal.</p>}
      <div className="flex items-center gap-2 flex-wrap">
        {!simulado && (
          <button type="button" onClick={onCambiarToken}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <KeyRound size={14} /> Cambiar token
          </button>
        )}
        <button type="button" onClick={onReconfigurar} disabled={ocupadoAuto}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {ocupadoAuto ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Reconfigurar en Twilio
        </button>
        <button type="button" onClick={onOtraCuenta}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Conectar otra cuenta
        </button>
      </div>
      <p className="text-xs text-emerald-800/80">«Reconfigurar en Twilio» vuelve a apuntar la entrada, los avisos y Event Streams a esta dirección (por ejemplo, después de cambiar el túnel o el dominio).</p>
    </div>
  );
}

/** «Cambiar token» (rotación, F26): sólo el Auth Token nuevo; se vacía apenas se manda. */
function FormularioToken({ onCerrar }: { onCerrar: () => void }) {
  const { recargar } = useCuentaOficial();
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [guardando, setGuardando] = useState(false);

  const guardar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (guardando) return;
    const e = validarTokenNuevo(token);
    if (e) { setError(e); return; }
    const t = token.trim();
    setToken("");
    setGuardando(true);
    setError("");
    try {
      const l = leerRespuesta<CuentaPublica>(await oficialApi.rotarToken(t));
      if (!l.ok) { setError(textoErrorConexion({ ...l, codigoTwilio: l.extra.codigoTwilio })); return; }
      setOk("Listo: el token nuevo quedó guardado. Twilio ya lo valida.");
      await recargar();
    } catch (err) {
      const x = leerError(err);
      setError(textoErrorConexion({ ...x, codigoTwilio: x.body.codigoTwilio }));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={guardar} autoComplete="off" className="rounded-xl border border-slate-100 p-4 space-y-3">
      <p className="text-sm text-slate-700">Si rotaste el Auth Token en la consola, pegá el nuevo acá. No hace falta cargar nada más.</p>
      <Campo label="Auth Token nuevo" tipo="password" value={token} onChange={(v) => { setToken(v.trim()); setError(""); setOk(""); }} placeholder="32 caracteres" error={error || undefined} />
      {ok && <p role="status" className="text-sm text-emerald-700 inline-flex items-center gap-1.5"><CheckCircle2 size={15} /> {ok}</p>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={guardando || !token}
          className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
          {guardando ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Guardar token
        </button>
        <button type="button" onClick={onCerrar} className="text-sm font-semibold text-slate-600 hover:text-slate-900">Cerrar</button>
      </div>
    </form>
  );
}

function SelectNumero({ desc, valor, onChange }: { desc: Descubrimiento; valor: string; onChange: (v: string) => void }) {
  const id = useId();
  const opciones = desc.numeros.map(opcionNumero);
  const elegido = opciones.find((o) => o.valor === valor);
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">Número de WhatsApp</label>
      <select id={id} value={valor} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
        <option value="">{opciones.length ? "Elegí un número" : "No hay números en la cuenta"}</option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor} disabled={o.deshabilitado}>
            {o.texto}{o.deshabilitado ? ` — ${o.motivo}` : ""}
          </option>
        ))}
      </select>
      {elegido?.deshabilitado && <p className="text-xs text-red-600 mt-1">{elegido.motivo}</p>}
      {opciones.some((o) => o.deshabilitado) && (
        <ul className="mt-1 space-y-0.5">
          {opciones.filter((o) => o.deshabilitado).map((o) => (
            <li key={o.valor} className="text-xs text-slate-500">No disponible: {o.texto.split(" · ")[0]} — {o.motivo}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SelectServicio({ desc, valor, onChange, marca }: { desc: Descubrimiento; valor: string; onChange: (v: string) => void; marca: string }) {
  const id = useId();
  const elegido = desc.servicios.find((s) => s.sid === valor);
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">Messaging Service</label>
      <select id={id} value={valor} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
        <option value="">Elegí un servicio</option>
        {desc.servicios.map((s) => (
          <option key={s.sid} value={s.sid}>{s.nombre || s.sid}{s.numeros.length ? ` · ${s.numeros.length} ${s.numeros.length === 1 ? "número" : "números"}` : ""}</option>
        ))}
        <option value={SERVICIO_NUEVO}>Crear uno nuevo: «{nombreServicioNuevo(marca)}»</option>
      </select>
      {elegido && elegido.entradaUrl && !elegido.entradaEsNuestra && (
        <p className="text-xs text-amber-800 mt-1">Este servicio ya manda las respuestas a otra dirección: te vamos a preguntar antes de cambiarla.</p>
      )}
    </div>
  );
}

function Campo({ label, value, onChange, placeholder, error, tipo = "text" }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; error?: string; tipo?: "text" | "password";
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label>
      <input id={id} type={tipo} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        spellCheck={false} autoComplete="off" aria-invalid={!!error || undefined}
        className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 ${error ? "border-red-300" : "border-slate-200"}`} />
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export default AsistenteConexion;
